import { Injectable } from '@angular/core';
import {
  AES,
  Base64,
  CBC,
  Hex,
  HmacSHA256,
  PBKDF2,
  Pkcs7,
  SHA256,
  SHA256Algo,
  Utf8,
  WordArray,
} from 'crypto-es';

export class CorruptedDataError extends Error {
  constructor(
    message: string,
    public readonly context?: string,
  ) {
    super(message);
    this.name = 'CorruptedDataError';
  }
}

@Injectable()
export class EnhancedEncryptionService {
  // Encryption constants
  private readonly PBKDF2_ITERATIONS = 100000;
  private readonly KEY_LENGTH = 256;
  private readonly IV_LENGTH = 16;
  private readonly KEY_SIZE_DIVISOR = 32;
  private readonly SALT_RANDOM_BYTES = 16;
  private readonly EXPECTED_ENCRYPTED_PARTS = 4;
  private readonly PADDING_BYTES = 8;
  private readonly SCRAMBLE_SEED_BYTES = 4;
  private readonly SCRAMBLE_SEED_HEX_LENGTH = 8; // SCRAMBLE_SEED_BYTES * 2
  private readonly PADDING_HEX_LENGTH = 16; // PADDING_BYTES * 2
  private readonly EMPTY_DECRYPTION_BYTES = 0; // Indicates failed/empty decryption

  // Circuit breaker constants
  private readonly CIRCUIT_BREAKER_RESET_TIMEOUT = 5 * 60 * 1000; // 5 minutes
  private readonly CIRCUIT_BREAKER_FAILURE_THRESHOLD = 3; // Number of failures before opening circuit breaker
  private readonly CIRCUIT_BREAKER_MIN_BACKOFF = 1000; // 1 second minimum backoff
  private readonly CIRCUIT_BREAKER_MAX_BACKOFF = 30000; // 30 seconds maximum backoff
  private readonly CIRCUIT_BREAKER_JITTER_FACTOR = 0.1; // 10% jitter for backoff
  private readonly CIRCUIT_BREAKER_BACKOFF_BASE = 2; // Base for exponential backoff
  private readonly CIRCUIT_BREAKER_BACKOFF_MULTIPLIER = 1000; // Multiplier for exponential backoff
  private readonly CIRCUIT_BREAKER_RANDOM_BYTES = 4; // Bytes for secure random generation (32 bits)
  private readonly CIRCUIT_BREAKER_MAX_INT32 = 0x7fffffff; // Maximum 32-bit signed integer for normalization
  private readonly SECONDS_TO_MILLISECONDS = 1000; // Conversion factor

  // Hash generation constants
  private readonly HASH_DATA_PREFIX_LENGTH = 100; // Length of encrypted value prefix for hashing
  private readonly HASH_DATA_LENGTH = 16; // Length of data hash
  private readonly HASH_FACTORS_LENGTH = 8; // Length of factors hash
  private readonly HEX_RADIX = 16; // Radix for parsing hexadecimal

  // Delimiter constants
  private readonly DELIMITER_MIN_POSITION = 0; // Minimum valid delimiter position
  private readonly SLICE_OFFSET = 1; // Offset for slicing after delimiter

  private readonly circuitBreakerState = new Map<
    string,
    {
      failures: number;
      lastFailure: number;
      isOpen: boolean;
    }
  >();

  private isCircuitBreakerOpen(context: string): boolean {
    const state = this.circuitBreakerState.get(context);
    if (!state) return false;

    const now = Date.now();
    const timeSinceLastFailure = now - state.lastFailure;

    // Reset circuit breaker after timeout
    if (timeSinceLastFailure > this.CIRCUIT_BREAKER_RESET_TIMEOUT) {
      this.circuitBreakerState.delete(context);
      return false;
    }

    return state.isOpen;
  }

  private recordCircuitBreakerFailure(
    context: string,
    isCritical: boolean,
  ): void {
    const state = this.circuitBreakerState.get(context) || {
      failures: 0,
      lastFailure: 0,
      isOpen: false,
    };

    state.failures++;
    state.lastFailure = Date.now();

    // Open circuit breaker immediately for critical failures (corrupted data)
    // or after reaching the failure threshold for regular failures
    state.isOpen =
      isCritical || state.failures >= this.CIRCUIT_BREAKER_FAILURE_THRESHOLD;
    this.circuitBreakerState.set(context, state);
  }

  private deriveEncryptionKey(
    password: string,
    salt: string,
    additionalFactors: string[] = [],
  ): WordArray {
    const combinedFactors = [password, ...additionalFactors].join(':');
    const derivedKey = PBKDF2(combinedFactors, salt, {
      keySize: this.KEY_LENGTH / this.KEY_SIZE_DIVISOR,
      iterations: this.PBKDF2_ITERATIONS,
      hasher: SHA256Algo,
    });

    return derivedKey;
  }

  private generateDeviceSalt(): string {
    const randomBytes = WordArray.random(this.SALT_RANDOM_BYTES);
    const timestamp = Date.now().toString();

    return SHA256(timestamp + randomBytes).toString();
  }

  encrypt(
    value: string,
    password: string,
    additionalFactors: string[] = [],
  ): string {
    try {
      const salt = this.generateDeviceSalt();
      const key = this.deriveEncryptionKey(password, salt, additionalFactors);
      const iv = WordArray.random(this.IV_LENGTH);

      const encrypted = AES.encrypt(value, key, {
        iv: iv,
        mode: CBC,
        padding: Pkcs7,
      });

      const authTag = this.generateAuthenticationTag(value, key, iv);
      const encryptedPayload = `${salt}:${iv.toString()}:${authTag}:${encrypted.toString()}`;
      const obfuscated = this.obfuscateData(encryptedPayload);

      return obfuscated;
    } catch {
      throw new Error('Encryption failed');
    }
  }

  decrypt(
    encryptedValue: string,
    password: string,
    additionalFactors: string[] = [],
  ): string {
    const circuitContext = this.generateCircuitBreakerContext(
      encryptedValue,
      additionalFactors,
    );

    // Check circuit breaker before attempting decryption with exponential backoff
    if (this.isCircuitBreakerOpen(circuitContext)) {
      const state = this.circuitBreakerState.get(circuitContext);
      const timeSinceLastFailure = Date.now() - (state?.lastFailure ?? 0);
      const backoffDelay = this.getBackoffDelay(state?.failures ?? 0);

      // Allow retry with exponential backoff - don't completely block
      if (timeSinceLastFailure < backoffDelay) {
        const error = new CorruptedDataError(
          `Circuit breaker backoff active - retry in ${Math.round((backoffDelay - timeSinceLastFailure) / this.SECONDS_TO_MILLISECONDS)}s`,
          circuitContext,
        );
        throw error;
      }
    }

    try {
      this.validateDecryptionInputs(encryptedValue, password);
      const { salt, ivHex, authTag, encryptedData } =
        this.parseEncryptedData(encryptedValue);
      const key = this.deriveEncryptionKey(password, salt, additionalFactors);
      const decryptedWordArray = this.performAESDecryption(
        encryptedData,
        key,
        ivHex,
      );
      const decrypted = this.convertToUtf8String(decryptedWordArray);
      this.verifyDataIntegrity(decrypted, key, ivHex, authTag);
      return decrypted;
    } catch (error) {
      const isCriticalError = this.isCriticalDecryptionError(error);
      this.recordCircuitBreakerFailure(circuitContext, isCriticalError);

      if (isCriticalError) {
        throw new CorruptedDataError(
          `Critical decryption failure: ${error instanceof Error ? error.message : 'Unknown error'}`,
          circuitContext,
        );
      }

      if (error instanceof Error) {
        throw new Error(`Decryption failed: ${error.message}`);
      } else {
        throw new Error('Decryption failed: Unknown error');
      }
    }
  }

  private validateDecryptionInputs(
    encryptedValue: string,
    password: string,
  ): void {
    if (!encryptedValue || !password) {
      throw new Error('Missing required parameters for decryption');
    }
  }

  private parseEncryptedData(encryptedValue: string): {
    salt: string;
    ivHex: string;
    authTag: string;
    encryptedData: string;
  } {
    const deobfuscated = this.deobfuscateData(encryptedValue);
    const parts = deobfuscated.split(':');

    if (parts.length !== this.EXPECTED_ENCRYPTED_PARTS) {
      throw new Error('Invalid encrypted data format');
    }

    const [salt, ivHex, authTag, encryptedData] = parts;

    if (!salt || !ivHex || !authTag || !encryptedData) {
      throw new Error('Missing encrypted data components');
    }

    return { salt, ivHex, authTag, encryptedData };
  }

  private performAESDecryption(
    encryptedData: string,
    key: WordArray,
    ivHex: string,
  ): WordArray {
    try {
      const decrypted = AES.decrypt(encryptedData, key, {
        iv: Hex.parse(ivHex),
        mode: CBC,
        padding: Pkcs7,
      });

      if (decrypted.sigBytes === this.EMPTY_DECRYPTION_BYTES) {
        throw new Error('Decryption failed');
      }

      return decrypted;
    } catch {
      throw new Error('Decryption failed');
    }
  }

  private convertToUtf8String(decryptedWordArray: WordArray): string {
    try {
      const decrypted = decryptedWordArray.toString(Utf8);

      if (!decrypted) {
        throw new Error(
          'Decryption resulted in empty string - likely incorrect password',
        );
      }

      return decrypted;
    } catch (error) {
      if (error instanceof Error) {
        error.message =
          'UTF-8 conversion failed - authentication factors may have changed or data is corrupted. Please sign in again.';
        error.name = 'AuthenticationFactorsChangedError';
        throw error;
      }

      throw new Error(
        'UTF-8 conversion failed - authentication factors may have changed or data is corrupted. Please sign in again.',
      );
    }
  }

  private verifyDataIntegrity(
    decrypted: string,
    key: WordArray,
    ivHex: string,
    authTag: string,
  ): void {
    if (
      !this.verifyAuthenticationTag(decrypted, key, Hex.parse(ivHex), authTag)
    ) {
      throw new Error(
        'Data integrity verification failed - data may be corrupted or key is incorrect',
      );
    }
  }

  private generateAuthenticationTag(
    data: string,
    key: WordArray,
    iv: WordArray,
  ): string {
    const hmacInput = data + iv.toString();
    const hmac = HmacSHA256(hmacInput, key);
    return hmac.toString();
  }

  private verifyAuthenticationTag(
    data: string,
    key: WordArray,
    iv: WordArray,
    expectedTag: string,
  ): boolean {
    const hmac = HmacSHA256(data + iv.toString(), key);
    return hmac.toString() === expectedTag;
  }

  private obfuscateData(data: string): string {
    const encoded = Base64.stringify(Utf8.parse(data));

    // Add random padding to mask length (8 bytes => 16 hex chars)
    const padding = WordArray.random(this.PADDING_BYTES).toString(Hex);
    const payload = encoded + padding;

    // Deterministically scramble with a seed we store alongside the payload
    const scrambleSeed = WordArray.random(this.SCRAMBLE_SEED_BYTES).toString(
      Hex,
    );
    const scrambled = this.scrambleString(payload, scrambleSeed);

    return `${scrambleSeed}:${scrambled}`;
  }

  private deobfuscateData(obfuscated: string): string {
    try {
      if (!obfuscated) {
        throw new Error('Empty obfuscated data');
      }

      let scrambleSeed: string | null = null;
      let payload = obfuscated;

      const delimiterIndex = obfuscated.indexOf(':');
      if (delimiterIndex > this.DELIMITER_MIN_POSITION) {
        const potentialSeed = obfuscated.slice(0, delimiterIndex);
        const seedPattern = new RegExp(
          `^[0-9a-fA-F]{${this.SCRAMBLE_SEED_HEX_LENGTH}}$`,
        );
        if (seedPattern.test(potentialSeed)) {
          scrambleSeed = potentialSeed.toLowerCase();
          payload = obfuscated.slice(delimiterIndex + this.SLICE_OFFSET);
        }
      }

      const unscrambled = scrambleSeed
        ? this.unscrambleString(payload, scrambleSeed)
        : payload;

      // Validate unscrambled data length
      if (unscrambled.length < this.PADDING_HEX_LENGTH) {
        throw new Error('Unscrambled data too short to contain padding');
      }

      // Remove padding (8 bytes => 16 hex chars) and decode
      const withoutPadding = unscrambled.slice(0, -this.PADDING_HEX_LENGTH);

      if (!withoutPadding) {
        throw new Error('No data remaining after padding removal');
      }

      try {
        const decoded = Base64.parse(withoutPadding);
        return decoded.toString(Utf8);
      } catch {
        throw new Error('Invalid base64 data in obfuscated payload');
      }
    } catch {
      throw new Error('Data deobfuscation failed');
    }
  }

  private scrambleString(str: string, seed: string): string {
    if (!str.length) {
      return str;
    }

    const shift = this.getScrambleShift(seed, str.length);
    const rotated = this.rotateLeft(str, shift);
    return rotated.split('').reverse().join('');
  }

  private unscrambleString(str: string, seed: string): string {
    if (!str.length) {
      return str;
    }

    const reversed = str.split('').reverse().join('');
    const shift = this.getScrambleShift(seed, str.length);
    return this.rotateRight(reversed, shift);
  }

  private getScrambleShift(seed: string, length: number): number {
    if (length === 0) {
      return 0;
    }

    const numericSeed = Number.parseInt(seed, this.HEX_RADIX);
    return numericSeed % length;
  }

  private rotateLeft(str: string, shift: number): string {
    if (!shift || shift % str.length === 0) {
      return str;
    }

    const normalized = shift % str.length;
    return str.slice(normalized) + str.slice(0, normalized);
  }

  private rotateRight(str: string, shift: number): string {
    if (!shift || shift % str.length === 0) {
      return str;
    }

    const normalized = shift % str.length;
    return str.slice(-normalized) + str.slice(0, -normalized);
  }

  oauthHashCodeVerifier(codeVerifier: string): string {
    // OAuth2 code challenge generation (SHA256 + URL-safe Base64)
    const hash = SHA256(codeVerifier);
    const base64 = Base64.stringify(hash);

    return base64.replaceAll('+', '-').replaceAll('/', '_').split('=')[0];
  }

  private generateCircuitBreakerContext(
    encryptedValue: string,
    additionalFactors: string[],
  ): string {
    // Use a hash of the first part of encrypted value to identify unique corrupted data
    // Don't use the full value for privacy, just enough to identify the specific corrupted data
    const dataHash = SHA256(
      encryptedValue.substring(0, this.HASH_DATA_PREFIX_LENGTH),
    )
      .toString()
      .substring(0, this.HASH_DATA_LENGTH);
    const factorsHash = SHA256(additionalFactors.join(':'))
      .toString()
      .substring(0, this.HASH_FACTORS_LENGTH);
    return `decrypt_${dataHash}_${factorsHash}`;
  }

  private isCriticalDecryptionError(error: any): boolean {
    const errorMessage = (error.message ?? String(error)).toLowerCase();

    const criticalIndicators = [
      'malformed utf-8',
      'invalid encrypted data format',
      'missing encrypted data components',
      'invalid base64 data',
      'data deobfuscation failed',
      'unscrambled data too short',
      'no data remaining after padding removal',
    ];

    return criticalIndicators.some((indicator) =>
      errorMessage.includes(indicator),
    );
  }

  private getBackoffDelay(failures: number): number {
    // Exponential backoff with jitter: min 1s, max 30s
    const baseDelay = Math.min(
      this.CIRCUIT_BREAKER_MAX_BACKOFF,
      Math.pow(this.CIRCUIT_BREAKER_BACKOFF_BASE, failures) *
        this.CIRCUIT_BREAKER_BACKOFF_MULTIPLIER,
    );

    const randomBytes = WordArray.random(this.CIRCUIT_BREAKER_RANDOM_BYTES);
    const secureRandom =
      Math.abs(randomBytes.words[0]) / this.CIRCUIT_BREAKER_MAX_INT32; // Normalize to 0-1
    const jitter =
      secureRandom * this.CIRCUIT_BREAKER_JITTER_FACTOR * baseDelay;

    return Math.max(this.CIRCUIT_BREAKER_MIN_BACKOFF, baseDelay + jitter);
  }
}
